import {
    FormControl,
    FormControlLabel,
    FormControlLabelProps,
    FormHelperText,
    Switch as MuiSwitch,
    SwitchProps as MuiSwitchProps
} from '@mui/material';
import { ReactNode } from 'react';
import {
    FieldPath, FieldValues, UseControllerProps, useController, useFormContext
} from 'react-hook-form';


type SwitchProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>
> = {
    label: ReactNode;
    labelClassName?: string;
    checkedLabel?: string;
    uncheckedLabel?: string;
    disableTypography?: FormControlLabelProps['disableTypography'];
    confirm?: (() => Promise<boolean>) | undefined;
} & UseControllerProps<TFieldValues, TName> & MuiSwitchProps;

/**
 * Renders a react-hook-form controlled mui Switch component.
 *
 * @example
 * ```ts
 * import { FormProvider, useForm } from 'react-hook-form';
 *
 * const formMethods = useForm();
 *
 * <FormProvider {...formMethods}>
 *     <Switch name="name" />
 * </FormProvider>
 * ```
 *
 * @param props - The props for the Controller and the underlying Switch
 * @constructor
 */
export function Switch<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    rules,
    name,
    label,
    checkedLabel,
    uncheckedLabel,
    className,
    labelClassName,
    defaultValue: defaultValueProp,
    disableTypography,
    confirm,
    onChange: onChangeProp,
    ...otherProps
}: SwitchProps<TFieldValues, TName>) {
    const {
        field: {
            ref, value, onChange, ...otherFieldProps
        },
        fieldState: { error }
    } = useController({
        name,
        rules,
        defaultValue: defaultValueProp || false as typeof defaultValueProp
    });

    const { setValue } = useFormContext();

    return (
        <FormControl
            className={className}
            component="span"
        >
            <FormControlLabel
                className={labelClassName}
                disableTypography={disableTypography}
                label={(!checkedLabel && !uncheckedLabel)
                    ? label
                    : value
                        ? checkedLabel!
                        : uncheckedLabel!}
                control={(
                    <MuiSwitch
                        {...otherProps}
                        {...otherFieldProps}
                        onChange={confirm
                            ? async () => await confirm() && setValue<FieldPath<FieldValues>>(name, !value)
                            : (...args) => {
                                onChange(...args);
                                onChangeProp?.(...args);
                            }}
                        inputRef={ref}
                        checked={value}
                    />
                )}
            />

            {error && (
                <FormHelperText error>
                    {error.message}
                </FormHelperText>
            )}
        </FormControl>
    );
}
